/**
 * AriaConsole — Multi-Aria Dashboard
 *
 * URL: /app/aria/console (default landing su /app/aria)
 *
 * Mostra TUTTE le Aria del proprietario in un'unica vista, con:
 *   • KPI globali (conv oggi, lead totali/oggi)
 *   • Card per ogni Aria (status, sito, persona, stats live)
 *   • Pulsante "Nuova Aria" → /aria/crea
 *   • Pulsante "Brand & Policies" → /app/aria/account
 *
 * Click su una card → setActiveToken + naviga a /app/aria/installazione.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Sparkles, Plus, ArrowRight, Globe, Inbox, MessageCircle,
    Brain, RefreshCw, Layers,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAriaCustomer } from "./AriaCustomerArea";

export default function AriaConsole() {
    const ctx = useAriaCustomer();
    const nav = useNavigate();
    const [data, setData] = useState({ items: [], totals: {} });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const reload = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/widget/me/console");
            setData(d);
            setError("");
        } catch (e) { setError(formatApiError(e)); }
        setLoading(false);
    };

    useEffect(() => { reload(); }, []);

    const totals = data.totals || {};
    const kpis = useMemo(() => [
        { label: "Aria attive", value: totals.widgets || 0, icon: Sparkles, color: "#A855F7" },
        { label: "Conversazioni oggi", value: totals.conversations_today || 0, icon: MessageCircle, color: "#4F8BFF" },
        { label: "Lead oggi", value: totals.leads_today || 0, icon: Inbox, color: "#86EFAC" },
        { label: "Lead totali", value: totals.leads_total || 0, icon: Layers, color: "#FFC36B" },
    ], [totals]);

    const openAria = (token) => {
        ctx?.setActiveToken?.(token);
        try { localStorage.setItem("aria.active_widget", token); } catch (e) { /* noop */ }
        nav("/app/aria/installazione");
    };

    return (
        <div className="px-6 py-7 max-w-6xl mx-auto" data-testid="aria-console">
            <div className="flex items-center justify-between mb-7 flex-wrap gap-3">
                <div>
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        Multi-Aria Console · La tua network
                    </div>
                    <h1 className="text-white text-[28px]" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>
                        Le tue Aria
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => nav("/app/aria/account")}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12.5px] text-white/85 hover:text-white transition"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        data-testid="btn-account-knowledge">
                        <Brain size={13} /> Brand &amp; Policies
                    </button>
                    <button type="button" onClick={reload}
                        className="p-2.5 rounded-full text-white/55 hover:text-white"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        title="Ricarica" data-testid="btn-reload-console">
                        <RefreshCw size={13} />
                    </button>
                    <button type="button" onClick={() => nav("/aria/crea")}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12.5px] text-white"
                        style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }}
                        data-testid="btn-new-aria">
                        <Plus size={13} /> Nuova Aria
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
                {kpis.map((k) => (
                    <div key={k.label} className="rounded-xl p-4"
                        style={{ background: "rgba(15,18,28,0.5)", border: "1px solid rgba(255,255,255,0.06)" }}
                        data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {k.label}
                            </div>
                            <k.icon size={13} style={{ color: k.color }} />
                        </div>
                        <div className="text-white text-[26px]" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>
                            {loading ? "…" : k.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Lista Aria */}
            <div className="space-y-3">
                {loading && <div className="text-white/45 text-[12.5px] text-center py-8">caricamento…</div>}
                {!loading && data.items.length === 0 && (
                    <div className="rounded-2xl p-10 text-center" style={{ background: "rgba(15,18,28,0.4)", border: "1px dashed rgba(255,255,255,0.1)" }}>
                        <div className="w-12 h-12 rounded-xl mx-auto grid place-items-center mb-4" style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)" }}>
                            <Sparkles size={16} color="#fff" />
                        </div>
                        <div className="text-white text-[15px] mb-2" style={{ fontWeight: 500 }}>Non hai ancora nessuna Aria</div>
                        <div className="text-white/55 text-[12.5px] mb-4">Crea la tua prima Aria e installala su un sito in 60 secondi.</div>
                        <button type="button" onClick={() => nav("/aria/crea")}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12.5px] text-white"
                            style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }}
                            data-testid="empty-create-aria">
                            <Plus size={13} /> Crea la mia prima Aria
                        </button>
                    </div>
                )}
                {!loading && data.items.map((w) => (
                    <button key={w.widget_token} type="button" onClick={() => openAria(w.widget_token)}
                        className="w-full text-left rounded-2xl p-5 flex items-center gap-4 flex-wrap transition hover:scale-[1.005]"
                        style={{ background: "rgba(15,18,28,0.6)", border: `1px solid ${w.primary_color}30`, opacity: w.active ? 1 : 0.55 }}
                        data-testid={`aria-card-${w.widget_token}`}>
                        <div className="w-12 h-12 rounded-xl grid place-items-center flex-none" style={{ background: `linear-gradient(135deg, ${w.primary_color}30, ${w.primary_color}10)`, border: `1px solid ${w.primary_color}40` }}>
                            <Sparkles size={15} style={{ color: w.primary_color }} />
                        </div>
                        <div className="flex-1 min-w-[260px]">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-white text-[15px]" style={{ fontWeight: 500 }}>{w.persona_name}</span>
                                <Pill mono color="#9EC3FF">{w.vertical || "—"}</Pill>
                                {!w.active && <Pill color="#FF7AA3">disattiva</Pill>}
                            </div>
                            <div className="flex items-center gap-2 text-white/55 text-[12px] flex-wrap" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                <Globe size={11} />
                                <span>{w.site_url || w.site_name || "—"}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-5 flex-none text-[11.5px]">
                            <Stat label="Conv oggi" value={w.stats?.conversations_today || 0} color="#4F8BFF" />
                            <Stat label="Lead oggi" value={w.stats?.leads_today || 0} color="#86EFAC" />
                            <Stat label="Lead totali" value={w.stats?.leads_total || 0} color="#FFC36B" />
                            <ArrowRight size={14} className="text-white/40" />
                        </div>
                    </button>
                ))}
            </div>

            {/* Footer hint multi-modulo */}
            {!loading && data.items.length > 0 && (
                <div className="mt-7 p-4 rounded-2xl text-[12px]" style={{ background: "rgba(168,85,247,0.06)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(168,85,247,0.2)" }}>
                    <span className="text-[#C7A4FF]" style={{ fontWeight: 500 }}>💡 Sapevi che?</span> Tutte le Aria condividono il <strong>cervello</strong> del tuo account
                    (Brand &amp; Policies). Aggiornalo una volta sola e ogni Aria lo eredita istantaneamente.
                </div>
            )}
        </div>
    );
}

function Pill({ children, color, mono }) {
    return (
        <span className="text-[9.5px] px-1.5 py-0.5 rounded uppercase tracking-[0.2em]"
            style={{ background: `${color}22`, color, fontFamily: mono ? "JetBrains Mono, monospace" : "Geist, sans-serif", fontWeight: mono ? 500 : 600 }}>
            {children}
        </span>
    );
}

function Stat({ label, value, color }) {
    return (
        <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</div>
            <div className="text-[18px]" style={{ color, fontWeight: 400 }}>{value}</div>
        </div>
    );
}
